import { useCallback, useEffect, useRef, useState } from 'react';
import { useClient } from 'urql';

import { JobDocument } from '@/graphql/subscriptions/generated/jobStatus';
import { JobEntityStatusEnum, LanguageEnum } from '@/types/schema';

interface Params {
  languageConfigs: any;
}

const initialJobStatusValue = {} as Record<LanguageEnum, JobEntityStatusEnum>;

const useSubscribeToJobUpdates = ({ languageConfigs }: Params) => {
  const client = useClient();
  const subscribed = useRef(new Set<LanguageEnum>());

  const [jobStatus, setJobStatus] =
    useState<Record<LanguageEnum, JobEntityStatusEnum>>(initialJobStatusValue);

  const subscribeToJobUpdates = useCallback(
    (jobScheduleId: string, language: LanguageEnum) => {
      const { unsubscribe } = client
        .subscription(
          JobDocument,
          { data: { jobScheduleId } },
          { requestPolicy: 'cache-and-network' },
        )
        .subscribe(({ data }) => {
          const status = data?.job?.status;
          if (status) {
            setJobStatus((prev) => ({ ...prev, [language]: status }));
          }
        });

      return unsubscribe;
    },
    [client],
  );

  useEffect(() => {
    if (!languageConfigs?.length) {
      return;
    }

    const newJobStatus = {} as Record<LanguageEnum, JobEntityStatusEnum>;
    const unsubscribers: (() => void)[] = [];

    languageConfigs.forEach(({ languageType, jobScheduleId, jobStatus }: any) => {
      if (languageType && jobStatus) {
        // @ts-ignore
        newJobStatus[languageType] = jobStatus;

        if (
          jobStatus === JobEntityStatusEnum.InProcess &&
          jobScheduleId &&
          !subscribed.current.has(languageType)
        ) {
          const unsubscribe = subscribeToJobUpdates(jobScheduleId, languageType);
          subscribed.current.add(languageType);
          unsubscribers.push(unsubscribe);
        }
      }
    });

    setJobStatus(newJobStatus);

    return () => {
      unsubscribers.forEach((unsub) => unsub());
      subscribed.current.clear();
    };
  }, [languageConfigs, subscribeToJobUpdates]);

  return { jobStatus };
};

export default useSubscribeToJobUpdates;
